



















































































































































































































































































































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import InfiniteLoading, { StateChanger } from "vue-infinite-loading";
import {
  getTimeTrackingMonth,
  onTimeTrackingMonthSnapshot,
  getMonthDocId,
  getDayEntry,
  resolveSharedEntriesForDay,
  mergeSharedProjectEntries,
  buildDisplayEntries,
  buildDaySummary,
  dayHasEntries,
  formatMinutesToHours,
  calcDurationMinutes,
  formatDuration,
  detectActiveAndLastProjectFromDay,
  onProjectTimeTrackingLogsSnapshot,
  getProjectLogTaskLabel,
  computeOverlapMarkers,
  deriveEntryState,
  groupMergeableRuns,
  MergeCandidate,
  ProjectTimeTrackingLogsSource,
} from "@/data/repository/TimeTrackingRepository";
import {
  TimeTrackingDayEntry,
  TimeTrackingMonth,
  TimeTrackingDisplayEntry,
  TimeTrackingDaySummary,
  TimeTrackingEntryState,
  TimelineSubEntry,
  ProjectTimeTrackingLog,
} from "@/data/models/TimeTracking";
import { ProjectOperationSource } from "@/data/models/ProjectDetails";
import { findUser } from "@/data/repository/UserRepository";
import { getActiveUser } from "@/data/repository/UserRepository";
import { endViewAs, getViewAsUser, startViewAs } from "@/utils/ViewAs";
import { UserModule } from "@/store/modules/UserModule";
import {
  switchTargetUserAccess,
  expandMergedTimeTrackingEntriesAccess,
} from "@/permissions/UserPermissions";
import { manageProjectsClientAccess } from "@/permissions/ClientPermissions";
import { getProjectManagementType } from "@/data/repository/ClientRepository";
import { ProjectManagement } from "@/data/models/Client";
import router from "@/router/router";
import { getSelectableTargetUsers } from "@/utils/TargetUserSelection";
import UserPickerDialog from "@/components/common/UserPickerDialog.vue";
import { ProjectsModule } from "@/store/modules/ProjectsModule";
import { getProjectDetails } from "@/data/repository/ProjectRepository";
import { getMinGeneralGapDurationInMin } from "@/data/repository/SettingsRepository";
import { formatJsTime, formatJsDate } from "@/utils/DateFormatter";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed, themedFill, themedFillTextColor } from "@/utils/Theme";
import {
  COLOR_TIME_TRACKING_CHIP_PROJECT,
  COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE,
  COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE_TEXT,
  COLOR_TIME_TRACKING_CHIP_GENERAL,
  COLOR_TIME_TRACKING_CHIP_PAUSE,
  COLOR_TIME_TRACKING_CHIP_TRAVEL,
  COLOR_TIME_TRACKING_CHIP_TRAVEL_ACTIVE,
  COLOR_TIME_TRACKING_CHIP_TRAVEL_ACTIVE_TEXT,
  COLOR_TIME_TRACKING_CHIP_TRAVEL_PROJECT,
  COLOR_TIME_TRACKING_CHIP_INTERRUPTION,
  COLOR_TIME_TRACKING_CHIP_PROJECT_NAME,
  COLOR_TIME_TRACKING_DAY_CLOSED,
  COLOR_TIME_TRACKING_DAY_OPEN,
  COLOR_SELECTED_NON_DEFAULT_TOOLBAR_FILTER_BACKGROUND,
  COLOR_TIME_TRACKING_EDITED_TIME,
  COLOR_PROJECT_TYPE_ARCHIVED,
  COLOR_PROJECT_TYPE_DELETED,
} from "@/utils/colors";
import { DateTime } from "luxon";
import { getEffectiveUser } from "@/utils/ViewAs";

const PAGE_SIZE = 20;

type DayHighlight = "none" | "green" | "yellow";

interface WeekDay {
  date: string;
  dayNum: number;
  isSelected: boolean;
  isToday: boolean;
  isFuture: boolean;
  highlight: DayHighlight;
}

/** A flat row for the table. */
interface TableRow {
  key: string;
  entry: TimeTrackingDisplayEntry;
  subEntry?: TimelineSubEntry;
  projectName?: string;
  /**
   * Present on a collapsed same-entry merge head: the original rows this row was
   * merged from (in order). Rendered on demand when the head is expanded.
   */
  mergedChildren?: TableRow[];
  /** True when this row is a merge head's child, revealed while the head is expanded. */
  isMergeChild?: boolean;
}

/** A Row 2 project-log row, wrapping a (possibly merged) project log. */
interface ProjectLogRow {
  key: string;
  log: ProjectTimeTrackingLog;
  /** Present on a collapsed merge head: the original logs it was merged from (in order). */
  mergedChildren?: ProjectTimeTrackingLog[];
  /** True when this row is a merge head's child, revealed while the head is expanded. */
  isMergeChild?: boolean;
}

interface BoundaryMarkers {
  start: boolean;
  end: boolean;
}

@Component({
  name: "TimeTrackingHistory",
  components: {
    InfiniteLoading,
    "user-picker": UserPickerDialog,
  },
})
export default class TimeTrackingHistory extends Vue {
  @Ref("userPickerDialog") readonly userPickerDialog!: UserPickerDialog;

  selectedDate: string = new Date().toISOString().substring(0, 10);
  /** The Monday of the current week, used as anchor for the visible period. */
  periodAnchor: string = DateTime.local().startOf("week").toISODate() || '';
  dateMenu = false;
  isLoading = false;
  monthData: TimeTrackingMonth | null = null;
  /**
   * Month docs for the strip months OTHER than the selected date's month, keyed by
   * monthDocId. Powers day-strip coloring when the visible period crosses a month
   * boundary (the selected month is served by `monthData`; the adjacent month(s) here).
   */
  otherStripMonths: Record<string, TimeTrackingMonth | null> = {};
  displayEntries: TimeTrackingDisplayEntry[] = [];
  daySummary: TimeTrackingDaySummary | null = null;
  sliceLength = PAGE_SIZE;
  /**
   * Expansion state for collapsed same-entry merge heads (Row 1 + Row 2), keyed by
   * the head row's `key`. IRES-only feature: standard clients see merged rows but
   * never toggle them. A plain reassigned object keeps Vue 2 reactivity simple.
   */
  expandedMergeKeys: Record<string, boolean> = {};
  infiniteId = 0;
  private unsubscribeMonth: (() => void) | null = null;
  /** Realtime listeners for the adjacent strip month(s), keyed by monthDocId. */
  private otherStripUnsubscribes: Record<string, () => void> = {};
  /** Reactive current time, updated every 60 s for live duration display on open-ended entries. */
  now: Date = new Date();
  private nowTimer: ReturnType<typeof setInterval> | null = null;

  // ── Row 2: Project-level data (single displayed project) ──
  /** Detected active project from current month scan. */
  activeProjectId: string | null = null;
  activeProjectName: string | null = null;
  /** Detected last project (fallback when no active). */
  lastProjectId: string | null = null;
  lastProjectName: string | null = null;
  /** User-clicked project from Row 1 (overrides active/last). */
  clickedProjectId: string | null = null;
  clickedProjectName: string | null = null;
  /** Logs and loading state for the single displayed project. */
  displayedProjectLogs: ProjectTimeTrackingLog[] = [];
  /** Whether the displayed project's logs came from the live project or its archived copy. */
  displayedProjectLogsSource: ProjectTimeTrackingLogsSource =
    ProjectTimeTrackingLogsSource.LIVE;
  isLoadingDisplayedProject = false;
  private unsubscribeDisplayedProject: (() => void) | null = null;
  /** Cache: userId → display name. */
  private userNameCache: Record<string, string> = {};
  /** Cache: projectId → project name. */
  private projectNameCache: Record<string, string> = {};
  /** Cache: projectId → preferred project number (office -> ires). */
  private projectNumberCache: Record<string, string> = {};
  /** Cache: projectId → row 1 project label (project number fallback chain). */
  private row1ProjectLabelCache: Record<string, string> = {};

  // ── Target user (Sicht auf) ──
  private activeUser = getActiveUser();

  /** The effective user ID for data fetching (target user or logged-in user). */
  get effectiveUserId(): string {
    return getViewAsUser()?.id ?? this.activeUser?.id ?? "Master";
  }

  /** Whether "Sicht auf" shows another user; the app bar's chip names them (ticket 2610050). */
  get hasSelectedTargetUser(): boolean {
    return getViewAsUser() !== null;
  }

  /** Users the "Sicht auf" picker can offer, excluding the session user. */
  get selectableTargetUsers() {
    return getSelectableTargetUsers(UserModule.users, this.activeUser);
  }

  /**
   * Whether the current user has permission to use the user picker and there is
   * anyone to pick. Like the branch selector, the icon is hidden when the picker
   * would be empty, but stays visible while a target user is selected, since
   * then it is the only way back to the own view.
   */
  get showTargetUserSelector(): boolean {
    if (!switchTargetUserAccess(this.activeUser)) {
      return false;
    }

    return this.hasSelectedTargetUser || this.selectableTargetUsers.length > 0;
  }

  get targetUserIconStyle(): string {
    return this.hasSelectedTargetUser
      ? `background-color: ${COLOR_SELECTED_NON_DEFAULT_TOOLBAR_FILTER_BACKGROUND}`
      : "";
  }

  @Watch("effectiveUserId")
  onEffectiveUserChanged() {
    this.teardownProjectSubscriptions();
    this.clickedProjectId = null;
    this.clickedProjectName = null;
    this.activeProjectId = null;
    this.activeProjectName = null;
    this.lastProjectId = null;
    this.lastProjectName = null;
    this.displayedProjectLogs = [];
    this.displayedProjectLogsSource = ProjectTimeTrackingLogsSource.LIVE;
    this.isLoadingDisplayedProject = false;
    this.subscribeToMonth();
  }

  get currentLocale(): string {
    return this.$i18n.locale;
  }

  get tableHeaderColor(): string {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get projectNameChipColor(): string {
    return COLOR_TIME_TRACKING_CHIP_PROJECT_NAME;
  }

  /**
   * A chip or day-button fill for the active theme: the dark palette shade in dark mode.
   *
   * Applied in the template, after the getters that pick the light colour, because the text-colour
   * switches below compare those light constants by value.
   */
  fillColor(lightColor: string | undefined): string | undefined {
    return lightColor === undefined ? undefined : themedFill(lightColor);
  }

  /**
   * The label for a {@link fillColor} fill: dark on a palette shade in dark mode, else `lightText`.
   * An outlined chip ({@link isOpenEntryChip}) gets none, so its label takes the chip's colour.
   */
  fillTextColor(lightColor: string | undefined, lightText: string): string | undefined {
    if (this.isOpenEntryChip(lightColor)) {
      return undefined;
    }

    return lightColor === undefined ? lightText : themedFillTextColor(lightColor, lightText);
  }

  /**
   * Whether a phase chip is drawn outlined. Light mode tells an entry that is still running or
   * suspended from a finished one by a pale versus a strong fill; the dark palette has one shade per
   * colour, so dark mode tells them apart by shape instead: filled when finished, outlined in the
   * same colour while open (as a not-started step tile is outlined). Light mode never outlines.
   */
  isOpenEntryChip(lightColor: string | undefined): boolean {
    return this.$vuetify.theme.dark &&
      (lightColor === COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE || lightColor === COLOR_TIME_TRACKING_CHIP_TRAVEL_ACTIVE);
  }

  get projectChipColor(): string {
    return COLOR_TIME_TRACKING_CHIP_PROJECT;
  }

  // ── Row 2: Displayed project computeds ──

  /** The project ID currently shown in Row 2: clicked > active > last. */
  get displayedProjectId(): string | null {
    return this.clickedProjectId ?? this.activeProjectId ?? this.lastProjectId;
  }

  /** The project name shown in Row 2, always resolved from the project document. */
  get displayedProjectName(): string | null {
    const id = this.displayedProjectId;
    if (!id) return null;
    const fallback = this.clickedProjectName ?? this.activeProjectName ?? this.lastProjectName;
    return this.resolveProjectName(id, fallback);
  }

  /** The project number shown in Row 2 with priority: office number -> IRES number. */
  get displayedProjectNumber(): string | null {
    const id = this.displayedProjectId;
    if (!id) return null;
    return this.resolveProjectNumber(id);
  }

  /**
   * Tag for a Row 2 project that is no longer live: its logs were read from the
   * `archivedProjects` copy, so the entries belong to an archived or deleted project.
   * `null` for a live project (no tag).
   */
  get displayedProjectStateTag(): string | null {
    switch (this.displayedProjectLogsSource) {
      case ProjectTimeTrackingLogsSource.ARCHIVED:
        return this.$t("timeTrackingHistory.projectArchived") as string;
      case ProjectTimeTrackingLogsSource.DELETED:
        return this.$t("timeTrackingHistory.projectDeleted") as string;
      default:
        return null;
    }
  }

  /** Tag colors match the project list legend (archived = light grey, deleted = black). */
  get displayedProjectStateTagColor(): string {
    return this.isDisplayedProjectDeleted
      ? COLOR_PROJECT_TYPE_DELETED
      : COLOR_PROJECT_TYPE_ARCHIVED;
  }

  get displayedProjectStateTagTextColor(): string {
    return this.isDisplayedProjectDeleted ? "white" : "black";
  }

  /** The black "deleted" tag needs the dark-mode outline; the light grey one does not. */
  get isDisplayedProjectDeleted(): boolean {
    return this.displayedProjectLogsSource === ProjectTimeTrackingLogsSource.DELETED;
  }

  /** Show plain project name only when it is different from the displayed project number chip. */
  get showDisplayedProjectName(): boolean {
    if (!this.displayedProjectName) return false;
    if (!this.displayedProjectNumber) return true;
    return this.displayedProjectName.trim() !== this.displayedProjectNumber.trim();
  }

  /** Whether the user manually clicked a project (not the auto-detected one). */
  get isShowingClickedProject(): boolean {
    return this.clickedProjectId != null;
  }

  /** Dynamic title for the Row 2 card. */
  get displayedProjectTitle(): string {
    if (this.clickedProjectId) {
      return this.$t("timeTrackingHistory.projectEntries") as string;
    }
    if (this.activeProjectId) {
      return this.$t("timeTrackingHistory.activeProjectEntries") as string;
    }
    return this.$t("timeTrackingHistory.lastProjectEntries") as string;
  }

  /** Label for the back button: "Zurück zum aktiven/letzten Projekt". */
  get backToDefaultProjectLabel(): string {
    if (this.activeProjectId) {
      return this.$t("timeTrackingHistory.backToActiveProject") as string;
    }
    return this.$t("timeTrackingHistory.backToLastProject") as string;
  }

  get selectedDateFormatted(): string {
    if (!this.selectedDate) return "";
    return DateTime.fromISO(this.selectedDate).toFormat("dd.MM.yyyy");
  }

  /** Number of days to show: 14 on wide screens, 7 on narrow. */
  get periodDayCount(): number {
    return this.$vuetify.breakpoint.mdAndUp ? 14 : 7;
  }

  /** Returns days for the date strip, anchored to periodAnchor (not selectedDate). */
  get periodDays(): WeekDay[] {
    const anchorMonday = DateTime.fromISO(this.periodAnchor);
    const startMonday = this.periodDayCount === 14
      ? anchorMonday.minus({ weeks: 1 })
      : anchorMonday;
    const today = DateTime.local().startOf("day");
    const days: WeekDay[] = [];
    for (let i = 0; i < this.periodDayCount; i++) {
      const d = startMonday.plus({ days: i });
      const isoDate = d.toISODate() || '';
      days.push({
        date: isoDate,
        dayNum: d.day,
        isSelected: isoDate === this.selectedDate,
        isToday: isoDate === today.toISODate(),
        isFuture: d > today,
        highlight: this.getDayHighlight(d.toJSDate()),
      });
    }
    return days;
  }

  get periodMonthLabel(): string {
    const anchorMonday = DateTime.fromISO(this.periodAnchor);
    const startMonday = this.periodDayCount === 14
      ? anchorMonday.minus({ weeks: 1 })
      : anchorMonday;
    return startMonday.toFormat("MMM yyyy");
  }

  get periodEndMonthLabel(): string {
    const anchorMonday = DateTime.fromISO(this.periodAnchor);
    const startMonday = this.periodDayCount === 14
      ? anchorMonday.minus({ weeks: 1 })
      : anchorMonday;
    const lastDay = startMonday.plus({ days: this.periodDayCount - 1 });
    if (lastDay.month !== startMonday.month) {
      return lastDay.year !== startMonday.year
        ? lastDay.toFormat("MMM yyyy")
        : lastDay.toFormat("MMM");
    }
    return "";
  }

  /**
   * Distinct months the visible strip touches, as a representative date + monthDocId each.
   * A 14-day strip spans at most two months (a month is >= 28 days), so this returns 1 or 2 entries.
   */
  get stripMonthDates(): { docId: string; date: Date }[] {
    const anchorMonday = DateTime.fromISO(this.periodAnchor);
    const startMonday = this.periodDayCount === 14
      ? anchorMonday.minus({ weeks: 1 })
      : anchorMonday;
    const lastDay = startMonday.plus({ days: this.periodDayCount - 1 });
    const startDate = startMonday.toJSDate();
    const endDate = lastDay.toJSDate();
    const startId = getMonthDocId(startDate);
    const endId = getMonthDocId(endDate);
    if (startId === endId) return [{ docId: startId, date: startDate }];
    return [{ docId: startId, date: startDate }, { docId: endId, date: endDate }];
  }

  /** Stable key over the strip's month docIds, watched to (re)subscribe when the span changes. */
  get stripMonthKey(): string {
    return this.stripMonthDates.map((m) => m.docId).join("|");
  }

  @Watch("stripMonthKey")
  onStripMonthsChanged() {
    this.subscribeToOtherStripMonths();
  }

  /** True when the selected week is the current week (navigating forward would show only future days). */
  get isAtCurrentWeek(): boolean {
    const anchorMonday = DateTime.fromISO(this.periodAnchor);
    const todayMonday = DateTime.local().startOf("week");
    return anchorMonday >= todayMonday;
  }

  get visibleEntries(): TimeTrackingDisplayEntry[] {
    return this.displayEntries.slice(0, this.sliceLength);
  }

  get startOfDayEntry(): TimeTrackingDisplayEntry | undefined {
    return this.displayEntries.find(e => e.type === "startOfDay");
  }

  get endOfDayEntry(): TimeTrackingDisplayEntry | undefined {
    return this.displayEntries.find(e => e.type === "endOfDay");
  }

  /**
   * Flattens entries into table rows. Project sub-entries become main rows.
   * One row per stored entry/sub-entry — no merging here (that is applied in
   * `tableRows`).
   */
  get baseTableRows(): TableRow[] {
    const rows: TableRow[] = [];
    for (const entry of this.visibleEntries) {
      if (entry.type === "startOfDay" || entry.type === "endOfDay") continue;
      // Hide completed zero-minute general gaps. Open-ended gaps (endTime null,
      // e.g. day started but no task yet) must stay visible — calcDurationMinutes
      // returns 0 for a null end, so they'd be wrongly swallowed without the guard.
      if (entry.type === "general" && entry.endTime != null && calcDurationMinutes(entry.startTime, entry.endTime) === 0) continue;
      if (entry.type === "project" && entry.subEntries && entry.subEntries.length > 0) {
        for (const sub of entry.subEntries) {
          rows.push({ key: sub.id, entry, subEntry: { ...sub }, projectName: entry.projectName });
        }
      } else {
        rows.push({ key: entry.id, entry, projectName: entry.projectName });
      }
    }
    return rows;
  }

  /** Effective end of a base row (sub-entry end if present, else entry end); null = open. */
  private rowEndDate(row: TableRow): Date | null {
    return row.subEntry ? row.subEntry.endTime : row.entry.endTime;
  }

  /** Effective start of a base row (sub-entry start if present, else entry start). */
  private rowStartDate(row: TableRow): Date | null {
    return row.subEntry ? row.subEntry.startTime : row.entry.startTime;
  }

  /** Maps a base Row 1 row to a merge candidate. Row 1 is single-user, so userId is constant. */
  private toRow1MergeCandidate(row: TableRow): MergeCandidate {
    const entry = row.entry;
    if (entry.type !== "project" || !entry.projectId) {
      // Non-project rows never merge and always break a run.
      return { projectId: null, phaseKey: null, subType: null, userId: null, startMs: 0, endMs: null };
    }
    const sub = row.subEntry;
    const subType = sub
      ? sub.type
      : entry.countsAsTravel
        ? "travel"
        : entry.countsAsPause
          ? "pause"
          : "work";
    const start = this.rowStartDate(row);
    const end = this.rowEndDate(row);
    return {
      projectId: entry.projectId,
      // Require BOTH the phase label and the task to match.
      phaseKey: `${entry.phaseLabel ?? ""}|${sub?.task ?? entry.task ?? ""}`,
      subType,
      userId: "self",
      startMs: start ? start.getTime() : 0,
      endMs: end ? end.getTime() : null,
    };
  }

  /** True when a base row is a project row eligible to participate in a merge run. */
  private isMergeableProjectRow(row: TableRow): boolean {
    return row.entry.type === "project" && !!row.entry.projectId;
  }

  /** True when a base row is a computed Allgemein (general) gap row. */
  private isGeneralRow(row: TableRow): boolean {
    return row.entry.type === "general";
  }

  /** Whether two project rows are the same entry (project + phase/task + segment) and both closed. */
  private rowsSameEntryClosed(a: TableRow, b: TableRow): boolean {
    const ca = this.toRow1MergeCandidate(a);
    const cb = this.toRow1MergeCandidate(b);
    return (
      ca.projectId != null &&
      ca.projectId === cb.projectId &&
      ca.phaseKey === cb.phaseKey &&
      ca.subType === cb.subType &&
      ca.endMs != null &&
      cb.endMs != null
    );
  }

  /** Forward empty gap (ms) between a row's end and the next row's start, or null when not forward/open. */
  private rowGapMs(prev: TableRow, next: TableRow): number | null {
    const prevEnd = this.rowEndDate(prev);
    const nextStart = this.rowStartDate(next);
    if (!prevEnd || !nextStart) return null;
    const gap = nextStart.getTime() - prevEnd.getTime();
    return gap >= 0 ? gap : null;
  }

  /** Builds one collapsed merge-head row from a run of matching project rows (children = members + absorbed gaps). */
  private buildMergedRow1(members: TableRow[], children: TableRow[]): TableRow {
    const first = members[0];
    const last = members[members.length - 1];
    const lastEnd = this.rowEndDate(last);
    const lastSub = last.subEntry;

    // NOTE: no `wasMergedBoundaryAdjusted` here — a merge is a legitimate display
    // collapse, not a data anomaly, so it must NOT raise a `*` marker. The expand
    // arrow is the only merge affordance.
    const mergedEntry: TimeTrackingDisplayEntry = {
      ...first.entry,
      endTime: lastEnd,
      endSource: lastSub ? lastSub.endSource ?? null : last.entry.endSource ?? null,
      wasEndEdited: lastSub ? lastSub.wasEndEdited : last.entry.wasEndEdited,
      // How the run ended is the LAST member's story: a silent suspend closing the
      // final member must surface on the collapsed head, not the first member's flag.
      silentlyInterrupted: lastSub
        ? lastSub.silentlyInterrupted
        : last.entry.silentlyInterrupted,
      isRunning: false,
      state: last.entry.state,
    };

    let mergedSub: TimelineSubEntry | undefined;
    if (first.subEntry) {
      mergedSub = {
        ...first.subEntry,
        endTime: lastEnd,
        endSource: lastSub ? lastSub.endSource ?? null : last.entry.endSource ?? null,
        wasEndEdited: lastSub ? lastSub.wasEndEdited : last.entry.wasEndEdited,
        silentlyInterrupted: lastSub
          ? lastSub.silentlyInterrupted
          : last.entry.silentlyInterrupted,
      };
    }

    return {
      key: `merged__${first.key}`,
      entry: mergedEntry,
      subEntry: mergedSub,
      projectName: first.projectName,
      mergedChildren: children,
    };
  }

  /**
   * Table rows with the same-entry merge applied (Row 1). Consecutive rows for the
   * SAME project + phase/task + segment kind, both closed, separated only by empty
   * time ≤ the configured gap threshold, collapse into one head row.
   *
   * An intervening **Allgemein** (computed gap) row is *transparent*: when the empty
   * gap it represents is ≤ threshold it is crossed and **absorbed** into the merge
   * (hidden at top level, shown as a child on expand); when > threshold it blocks the
   * merge and renders normally. Any other row (pause/travel/other/different phase/user)
   * always blocks. IRES users can expand a head to reveal its constituents; standard
   * clients see only the collapsed head. Merging is display-only (day summary unaffected).
   */
  get tableRows(): TableRow[] {
    const base = this.baseTableRows;
    const gapMs = getMinGeneralGapDurationInMin() * 60 * 1000;
    const rows: TableRow[] = [];

    let i = 0;
    while (i < base.length) {
      const startRow = base[i];
      if (!this.isMergeableProjectRow(startRow)) {
        rows.push(startRow);
        i++;
        continue;
      }

      const members: TableRow[] = [startRow]; // real project members of the run
      const absorbed: TableRow[] = []; // Allgemein rows crossed (absorbed) by the run
      let pendingGenerals: TableRow[] = []; // generals seen since the last real member
      let lastReal = startRow;

      let j = i + 1;
      for (; j < base.length; j++) {
        const nxt = base[j];
        if (this.isGeneralRow(nxt)) {
          pendingGenerals.push(nxt);
          continue;
        }
        const gap = this.rowGapMs(lastReal, nxt);
        if (
          this.isMergeableProjectRow(nxt) &&
          this.rowsSameEntryClosed(lastReal, nxt) &&
          gap != null &&
          gap <= gapMs
        ) {
          absorbed.push(...pendingGenerals);
          pendingGenerals = [];
          members.push(nxt);
          lastReal = nxt;
          continue;
        }
        break; // a non-general, non-matching row (or > threshold gap) ends the run
      }

      if (members.length === 1) {
        rows.push(startRow);
      } else {
        const children = [...members, ...absorbed].sort(
          (a, b) => (this.rowStartDate(a)?.getTime() ?? 0) - (this.rowStartDate(b)?.getTime() ?? 0)
        );
        const head = this.buildMergedRow1(members, children);
        rows.push(head);
        if (this.canExpandMerged && this.expandedMergeKeys[head.key]) {
          for (const child of children) {
            rows.push({ ...child, key: `${head.key}__child_${child.key}`, isMergeChild: true });
          }
        }
      }

      // Generals collected after the last merged member (not absorbed) render normally.
      for (const g of pendingGenerals) rows.push(g);
      i = j;
    }
    return rows;
  }

  /**
   * Whether the merged rows can be expanded/collapsed (IRES-only), for the user whose view this
   * shows: the chosen one while "Sicht auf" is active (ticket 2610052).
   */
  get canExpandMerged(): boolean {
    return expandMergedTimeTrackingEntriesAccess(getEffectiveUser());
  }

  /**
   * IRES-only: whether the verification `*` boundary markers (overlap / day-clamp /
   * adjusted-data / cross-day) and their tooltips are shown. Gated by the SAME IRES
   * capability as the expand arrow — standard clients see neither. This is internal
   * verification metadata, not something regular clients need.
   */
  get showVerificationMarkers(): boolean {
    return this.canExpandMerged;
  }

  /** Number of stored entries a merge head represents (0 when not a head; absorbed Allgemein gaps excluded). */
  mergedChildCount(row: TableRow | ProjectLogRow): number {
    const children = row.mergedChildren as Array<{ entry?: TimeTrackingDisplayEntry }> | undefined;
    if (!children) return 0;
    // Row 2 logs have no `entry`; Row 1 excludes absorbed Allgemein gap rows.
    return children.filter((c) => !c.entry || c.entry.type !== "general").length;
  }

  /** Whether a merge head is currently expanded. */
  isMergeExpanded(row: TableRow | ProjectLogRow): boolean {
    return !!this.expandedMergeKeys[row.key];
  }

  /** Toggles a merge head's expanded state (no-op for non-heads / non-IRES users). */
  toggleMergeExpand(row: TableRow | ProjectLogRow) {
    if (!this.canExpandMerged || !row.mergedChildren) return;
    this.expandedMergeKeys = {
      ...this.expandedMergeKeys,
      [row.key]: !this.expandedMergeKeys[row.key],
    };
  }

  /** Tooltip text for a merge head's expand control. */
  mergeExpandTooltip(row: TableRow | ProjectLogRow): string {
    return this.$t("timeTrackingHistory.mergedEntriesTooltip", {
      count: this.mergedChildCount(row),
    }).toString();
  }

  /** Effective start of a project log (user-overridden start if present, else recorded start). */
  private projectLogStartDate(log: ProjectTimeTrackingLog): Date | null {
    const start = log.userSetStartOfWork ?? log.startedAtDate;
    return start ? start.toDate() : null;
  }

  /** Maps a project log to a merge candidate. Row 2 is single-project; user = startedByUserId. */
  private toRow2MergeCandidate(log: ProjectTimeTrackingLog): MergeCandidate {
    const start = this.projectLogStartDate(log);
    const end = this.projectLogEffectiveEndDate(log);
    return {
      projectId: this.displayedProjectId ?? null,
      phaseKey: `${log.projectPhase ?? ""}|${log.autoTimeTrackingContext ?? ""}`,
      subType: log.countsAsTravel ? "travel" : log.countsAsPause ? "pause" : "work",
      userId: log.startedByUserId ?? null,
      startMs: start ? start.getTime() : 0,
      endMs: end ? end.getTime() : null,
    };
  }

  /** Builds one collapsed merged log from a run of matching logs (end fields from the last). */
  private buildMergedLog(members: ProjectTimeTrackingLog[]): ProjectTimeTrackingLog {
    const first = members[0];
    const last = members[members.length - 1];
    return {
      ...first,
      endedAtDate: last.endedAtDate,
      userSetEndOfWork: last.userSetEndOfWork,
      endedBy: last.endedBy,
      endSource: last.endSource,
      endReason: last.endReason,
      suspendedAt: last.suspendedAt,
    };
  }

  /**
   * Row 2 project logs with the same-entry merge applied. Logs are sorted by effective
   * start, then consecutive logs for the same phase/task and same `startedByUserId`, both
   * closed and separated only by an empty gap ≤ threshold, collapse into one head. IRES
   * users can expand a head to reveal its constituent logs.
   */
  get displayedProjectLogRows(): ProjectLogRow[] {
    const logs = [...this.displayedProjectLogs].sort((a, b) => {
      const as = this.projectLogStartDate(a)?.getTime() ?? 0;
      const bs = this.projectLogStartDate(b)?.getTime() ?? 0;
      return as - bs;
    });
    const gapMs = getMinGeneralGapDurationInMin() * 60 * 1000;
    const runs = groupMergeableRuns(logs.map((l) => this.toRow2MergeCandidate(l)), gapMs);

    const rows: ProjectLogRow[] = [];
    for (const run of runs) {
      if (run.length === 1) {
        const log = logs[run[0]];
        rows.push({ key: log.id, log });
        continue;
      }
      const members = run.map((i) => logs[i]);
      const key = `merged__${members[0].id}`;
      rows.push({ key, log: this.buildMergedLog(members), mergedChildren: members });
      if (this.canExpandMerged && this.expandedMergeKeys[key]) {
        for (const child of members) {
          rows.push({ key: `${key}__child_${child.id}`, log: child, isMergeChild: true });
        }
      }
    }
    return rows;
  }

  get showInfiniteLoading(): boolean {
    return this.displayEntries.length > this.sliceLength;
  }

  /**
   * Resolves the loaded month document containing `jsDate`. The selected date's month is
   * served by `monthData`; other months visible in the strip come from `otherStripMonths`.
   * Returns null when that month has no loaded document.
   */
  private monthForDate(jsDate: Date): TimeTrackingMonth | null {
    const docId = getMonthDocId(jsDate);
    if (this.monthData && getMonthDocId(new Date(this.selectedDate)) === docId) {
      return this.monthData;
    }
    return this.otherStripMonths[docId] ?? null;
  }

  /** Determines highlight color for a day: green = closed+entries, yellow = open+entries. */
  getDayHighlight(jsDate: Date): DayHighlight {
    const month = this.monthForDate(jsDate);
    if (!month) return "none";
    const dayEntry = getDayEntry(month, jsDate);
    if (!dayEntry) return "none";
    if (!dayHasEntries(dayEntry)) return "none";
    return dayEntry.closedBy != null ? "green" : "yellow";
  }

  getDayButtonColor(day: WeekDay): string | undefined {
    if (day.isSelected) return "primary";
    if (day.highlight === "green") return COLOR_TIME_TRACKING_DAY_CLOSED;
    if (day.highlight === "yellow") return COLOR_TIME_TRACKING_DAY_OPEN;
    // Today (not selected, no entries) gets no color; its dark-mode visibility
    // is handled by the .date-strip-btn--today CSS rule (a color returned here
    // would be defeated by the --filled !important rule anyway).
    return undefined;
  }

  async mounted() {
    this.subscribeToMonth();
    this.nowTimer = setInterval(() => { this.now = new Date(); }, 60000);
  }

  activated() {
    this.scrollToBottom("row1Table");
    this.scrollToBottom("row2Table");
  }

  beforeDestroy() {
    if (this.unsubscribeMonth) {
      this.unsubscribeMonth();
      this.unsubscribeMonth = null;
    }
    this.teardownOtherStripMonths();
    if (this.nowTimer) {
      clearInterval(this.nowTimer);
      this.nowTimer = null;
    }
    this.teardownProjectSubscriptions();
  }

  async onDateSelected() {
    this.sliceLength = PAGE_SIZE;
    this.infiniteId++;
    // Reset clicked override whenever selected day changes.
    this.clickedProjectId = null;
    this.clickedProjectName = null;
    this.subscribeToMonth();
  }

  async onDatePickerInput() {
    this.dateMenu = false;
    this.periodAnchor = DateTime.fromISO(this.selectedDate).startOf("week").toISODate() || '';
    await this.onDateSelected();
  }

  selectDate(date: string) {
    this.selectedDate = date;
    this.onDateSelected();
  }

  goToPreviousPeriod() {
    const weeks = this.periodDayCount === 14 ? 2 : 1;
    const anchor = DateTime.fromISO(this.periodAnchor).minus({ weeks });
    this.periodAnchor = anchor.toISODate() || '';
    // Move selected date into the new visible period
    this.selectedDate = anchor.toISODate() || '';
    this.onDateSelected();
  }

  goToNextPeriod() {
    const weeks = this.periodDayCount === 14 ? 2 : 1;
    const anchor = DateTime.fromISO(this.periodAnchor).plus({ weeks });
    this.periodAnchor = anchor.toISODate() || '';
    this.selectedDate = anchor.toISODate() || '';
    this.onDateSelected();
  }

  goToToday() {
    this.selectedDate = new Date().toISOString().substring(0, 10);
    this.periodAnchor = DateTime.local().startOf("week").toISODate() || '';
    this.onDateSelected();
  }

  async loadDay() {
    this.isLoading = true;
    this.displayEntries = [];
    this.daySummary = null;

    try {
      const date = new Date(this.selectedDate);
      this.monthData = await getTimeTrackingMonth(date, this.effectiveUserId);
      await this.processMonthData();
    } finally {
      this.isLoading = false;
    }
  }

  /** Subscribes to realtime Firestore updates for the selected month. */
  private subscribeToMonth() {
    // Tear down previous listener
    if (this.unsubscribeMonth) {
      this.unsubscribeMonth();
      this.unsubscribeMonth = null;
    }

    this.isLoading = true;
    this.displayEntries = [];
    this.daySummary = null;

    const date = new Date(this.selectedDate);
    this.unsubscribeMonth = onTimeTrackingMonthSnapshot(date, (month) => {
      this.monthData = month;
      this.processMonthData().finally(() => {
        this.isLoading = false;
      });
    }, this.effectiveUserId);

    // Rebuild the adjacent-month listeners from scratch so an effective-user change
    // (same docIds, different data) re-subscribes for the new user.
    this.teardownOtherStripMonths();
    this.subscribeToOtherStripMonths();
  }

  /**
   * Subscribes to realtime updates for the strip months OTHER than the selected date's
   * month, so days on the far side of a month boundary are colored too. Idempotent:
   * adds listeners for newly visible months and drops those no longer in view.
   */
  private subscribeToOtherStripMonths() {
    const selectedDocId = getMonthDocId(new Date(this.selectedDate));
    const wanted = this.stripMonthDates.filter((m) => m.docId !== selectedDocId);
    const wantedIds = wanted.map((m) => m.docId);

    for (const docId of Object.keys(this.otherStripUnsubscribes)) {
      if (!wantedIds.includes(docId)) {
        this.otherStripUnsubscribes[docId]();
        delete this.otherStripUnsubscribes[docId];
        Vue.delete(this.otherStripMonths, docId);
      }
    }

    for (const { docId, date } of wanted) {
      if (this.otherStripUnsubscribes[docId]) continue;
      this.otherStripUnsubscribes[docId] = onTimeTrackingMonthSnapshot(
        date,
        (month) => Vue.set(this.otherStripMonths, docId, month),
        this.effectiveUserId
      );
    }
  }

  /** Tears down all adjacent strip-month listeners and clears their cached data. */
  private teardownOtherStripMonths() {
    for (const docId of Object.keys(this.otherStripUnsubscribes)) {
      this.otherStripUnsubscribes[docId]();
    }
    this.otherStripUnsubscribes = {};
    this.otherStripMonths = {};
  }

  /** Rebuilds display entries and day summary from the current monthData. */
  private async processMonthData() {
    const date = new Date(this.selectedDate);
    if (!this.monthData) {
      this.displayEntries = [];
      this.daySummary = null;
      this.updateProjectSubscriptions(null);
      return;
    }

    const dayEntry = getDayEntry(this.monthData, date);
    if (!dayEntry) {
      this.displayEntries = [];
      this.daySummary = null;
      this.updateProjectSubscriptions(null);
      return;
    }

    // Resolve + merge shared (team) entries so they appear in the timeline and day
    // summary like normal project entries. Guard against out-of-order async completion
    // if the selected day changed while the shared docs were being fetched.
    const requestedDate = this.selectedDate;
    const sharedById = await resolveSharedEntriesForDay(dayEntry);
    if (this.selectedDate !== requestedDate) {
      return;
    }
    const mergedDay = mergeSharedProjectEntries(dayEntry, sharedById);

    this.displayEntries = buildDisplayEntries(
      mergedDay,
      (key: string) => this.$t(key) as string
    );
    this.daySummary = buildDaySummary(mergedDay, this.displayEntries);
    this.updateProjectSubscriptions(mergedDay);
    this.scrollToBottom("row1Table");
  }

  infiniteHandler(state: StateChanger) {
    const nextSliceLength = this.sliceLength + PAGE_SIZE;
    this.sliceLength = nextSliceLength;

    if (nextSliceLength >= this.displayEntries.length) {
      state.complete();
    } else {
      state.loaded();
    }
  }

  formatTime(date: Date | null): string {
    if (!date) return "–";
    return formatJsTime(date);
  }

  private scrollToBottom(refName: "row1Table" | "row2Table", attempt = 0) {
    const MAX_SCROLL_RETRIES = 8;

    // Row 1 renders a sliced window grown by the infinite loader. Jumping to
    // the bottom must land on the day's LAST entry, so materialize all rows
    // first. (Previously the smooth-scroll retry cascade triggered the loader
    // page by page; the instant scroll reports "at bottom" of the initial
    // 20-row slice before the loader appends more, stranding the view mid-list.)
    if (refName === "row1Table" && this.sliceLength < this.displayEntries.length) {
      this.sliceLength = this.displayEntries.length;
    }

    // On initial page entry from sidebar, the table can render before layout is final.
    // Retry briefly until the scroll container has a stable scrollHeight.
    this.$nextTick(() => {
      this.$nextTick(() => {
        const table = this.$refs[refName] as Vue | undefined;
        const el = table?.$el as HTMLElement | undefined;

        if (!el) {
          if (attempt < MAX_SCROLL_RETRIES) {
            setTimeout(() => this.scrollToBottom(refName, attempt + 1), 50);
          }
          return;
        }

        const hasScrollableContent = el.scrollHeight > el.clientHeight + 1;
        // Always scroll instantly. Smooth programmatic scrolling desynchronized
        // Safari's (WebKit) mouse hit-testing from the scrolled content — after
        // the auto-scroll, clicks landed on rows' pre-scroll positions and opened
        // the wrong project. The 50ms retries also interrupted the in-flight
        // smooth animation. An instant jump keeps hit-testing consistent.
        el.scrollTo({ top: el.scrollHeight, behavior: "auto" });

        const atBottom = !hasScrollableContent || (el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
        if (!atBottom && attempt < MAX_SCROLL_RETRIES) {
          setTimeout(() => this.scrollToBottom(refName, attempt + 1), 50);
        }
      });
    });
  }

  private get selectedDayEnd(): Date {
    const dayEnd = new Date(this.selectedDate);
    dayEnd.setHours(23, 59, 59, 999);
    return dayEnd;
  }

  private capToSelectedDayEnd(date: Date): Date {
    return date.getTime() > this.selectedDayEnd.getTime()
      ? this.selectedDayEnd
      : date;
  }

  private getCappedDateInfo(date: Date): { value: Date; clamped: boolean } {
    if (date.getTime() > this.selectedDayEnd.getTime()) {
      return { value: this.selectedDayEnd, clamped: true };
    }
    return { value: date, clamped: false };
  }

  /**
   * Overlap (`*`) boundary markers for Row 1 project entries.
   * Minute precision (seconds ignored) via `computeOverlapMarkers`.
   */
  get row1ProjectBoundaryMarkers(): Record<string, BoundaryMarkers> {
    const items = this.displayEntries
      .filter((entry) => entry.type === "project" && !!entry.startTime)
      .map((entry) => ({
        id: entry.id,
        startMs: (entry.startTime as Date).getTime(),
        endMs: entry.endTime ? entry.endTime.getTime() : null,
      }));
    return computeOverlapMarkers(items, this.now.getTime());
  }

  /**
   * Overlap (`*`) boundary markers for Row 2 project logs. Minute precision.
   * Pause/travel logs are excluded from the check entirely — a pause/travel of one user
   * legitimately overlaps another user's task, so they neither get flagged nor flag others.
   */
  get row2ProjectLogBoundaryMarkers(): Record<string, BoundaryMarkers> {
    const items = this.displayedProjectLogs
      .filter((log) => !!log.startedAtDate && !log.countsAsPause && !log.countsAsTravel)
      .map((log) => {
        const end = this.projectLogEffectiveEndDate(log);
        return {
          id: log.id,
          startMs: (log.startedAtDate as firebase.firestore.Timestamp).toDate().getTime(),
          endMs: end ? end.getTime() : null,
        };
      });
    return computeOverlapMarkers(items, this.now.getTime());
  }

  // ── Row display helpers ──

  rowClass(): Record<string, boolean> {
    return {
      "entry-row": true,
    };
  }

  private markerReason(reason: "overlapStart" | "overlapEnd" | "dayClamped" | "crossDay" | "adjustedData" | "mergedBoundary"): string {
    return this.$t(`timeTrackingHistory.markerReasons.${reason}`) as string;
  }

  private formatMarkerReasonList(reasons: string[]): string | null {
    if (reasons.length === 0) return null;
    return Array.from(new Set(reasons)).join("\n");
  }

  private isTrailingOpenGeneralRow(row: TableRow): boolean {
    if (row.subEntry) return false;
    if (row.entry.type !== "general") return false;
    if (this.endOfDayEntry) return false;

    const visibleTableEntries = this.visibleEntries.filter(
      (entry) => entry.type !== "startOfDay" && entry.type !== "endOfDay"
    );
    const lastVisibleEntry = visibleTableEntries[visibleTableEntries.length - 1];
    return lastVisibleEntry?.id === row.entry.id;
  }

  rowTimeRangeReason(row: TableRow): string | null {
    if (!this.showVerificationMarkers) return null;
    if (this.isTrailingOpenGeneralRow(row)) return null;

    const reasons: string[] = [];
    const boundary = this.row1ProjectBoundaryMarkers[row.entry.id] ?? { start: false, end: false };

    if (row.subEntry) {
      const isEntryStartBoundary =
        !!row.entry.startTime &&
        !!row.subEntry.startTime &&
        row.subEntry.startTime.getTime() === row.entry.startTime.getTime();
      const isEntryEndBoundary =
        !!row.entry.endTime &&
        !!row.subEntry.endTime &&
        row.subEntry.endTime.getTime() === row.entry.endTime.getTime();

      if (boundary.start && isEntryStartBoundary) reasons.push(this.markerReason("overlapStart"));
      if (boundary.end && isEntryEndBoundary) reasons.push(this.markerReason("overlapEnd"));

      if (row.subEntry.endTime != null && this.getCappedDateInfo(row.subEntry.endTime).clamped) {
        reasons.push(this.markerReason("dayClamped"));
      }

      const mergedBoundaryMarker = row.subEntry.wasMergedBoundaryAdjusted === true;
      if (row.subEntry.wasDayClamped) {
        reasons.push(this.markerReason("adjustedData"));
      }
      if (mergedBoundaryMarker) {
        reasons.push(this.markerReason("mergedBoundary"));
      }

      return this.formatMarkerReasonList(reasons);
    }

    if (boundary.start) reasons.push(this.markerReason("overlapStart"));
    if (boundary.end) reasons.push(this.markerReason("overlapEnd"));
    if (row.entry.endTime != null && this.getCappedDateInfo(row.entry.endTime).clamped) {
      reasons.push(this.markerReason("dayClamped"));
    }
    const hasOverlapReason = boundary.start || boundary.end;
    if (row.entry.wasDayClamped && !hasOverlapReason) reasons.push(this.markerReason("adjustedData"));

    return this.formatMarkerReasonList(reasons);
  }

  rowDurationReason(row: TableRow): string | null {
    if (!this.showVerificationMarkers) return null;
    if (this.isTrailingOpenGeneralRow(row)) return null;

    const reasons: string[] = [];
    const boundary = this.row1ProjectBoundaryMarkers[row.entry.id] ?? { start: false, end: false };

    if (row.subEntry) {
      const isEntryStartBoundary =
        !!row.entry.startTime &&
        !!row.subEntry.startTime &&
        row.subEntry.startTime.getTime() === row.entry.startTime.getTime();
      const isEntryEndBoundary =
        !!row.entry.endTime &&
        !!row.subEntry.endTime &&
        row.subEntry.endTime.getTime() === row.entry.endTime.getTime();

      if (boundary.start && isEntryStartBoundary) reasons.push(this.markerReason("overlapStart"));
      if (boundary.end && isEntryEndBoundary) reasons.push(this.markerReason("overlapEnd"));

      const end = row.subEntry.endTime
        ? this.getCappedDateInfo(row.subEntry.endTime)
        : this.getCappedDateInfo(this.now);
      if (end.clamped) reasons.push(this.markerReason("dayClamped"));

      if (row.subEntry.wasDayClamped) {
        reasons.push(this.markerReason("adjustedData"));
      }
      if (row.subEntry.wasMergedBoundaryAdjusted === true) {
        reasons.push(this.markerReason("mergedBoundary"));
      }

      return this.formatMarkerReasonList(reasons);
    }

    if (boundary.start) reasons.push(this.markerReason("overlapStart"));
    if (boundary.end) reasons.push(this.markerReason("overlapEnd"));
    const end = row.entry.endTime
      ? this.getCappedDateInfo(row.entry.endTime)
      : this.getCappedDateInfo(this.now);
    if (end.clamped) reasons.push(this.markerReason("dayClamped"));
    const hasOverlapReason = boundary.start || boundary.end;
    if (row.entry.wasDayClamped && !hasOverlapReason) reasons.push(this.markerReason("adjustedData"));

    return this.formatMarkerReasonList(reasons);
  }

  /**
   * Returns structured time range parts for a table row.
   * Used by rowTimeRange (string assembly) and the template (colored span rendering).
   *
   * separator:
   *   ""   → no separator (silentInterruption shows start time only)
   *   " –" → open-ended (no end time to display)
   *   " – " → has end time (trailing space precedes the end time span)
   */
  private getRowTimeRangeData(row: TableRow): {
    startStr: string;
    startMarker: string;
    startEdited: boolean;
    separator: string;
    endStr: string;
    endMarker: string;
    endEdited: boolean;
  } {
    const boundary = this.row1ProjectBoundaryMarkers[row.entry.id] ?? { start: false, end: false };
    // IRES-only: all `*` verification markers are suppressed for standard clients.
    const showMarkers = this.showVerificationMarkers;

    if (row.subEntry) {
      const sub = row.subEntry;
      const isEntryStartBoundary =
        !!row.entry.startTime && !!sub.startTime &&
        sub.startTime.getTime() === row.entry.startTime.getTime();
      const isEntryEndBoundary =
        !!row.entry.endTime && !!sub.endTime &&
        sub.endTime.getTime() === row.entry.endTime.getTime();

      const startStr = this.formatTime(sub.startTime);
      const startMarker = showMarkers && boundary.start && isEntryStartBoundary ? "*" : "";

      if (sub.endTime == null) {
        return { startStr, startMarker, startEdited: sub.wasStartEdited, separator: " –", endStr: "", endMarker: "", endEdited: false };
      }

      const cappedEnd = this.getCappedDateInfo(sub.endTime);
      const inheritedEntryEndMarker = sub.wasMergedBoundaryAdjusted === true;
      const hasEndMarker = showMarkers && (cappedEnd.clamped || sub.wasDayClamped || inheritedEntryEndMarker ||
        (boundary.end && isEntryEndBoundary));

      return {
        startStr,
        startMarker,
        startEdited: sub.wasStartEdited,
        separator: " – ",
        endStr: this.formatTime(cappedEnd.value),
        endMarker: hasEndMarker ? "*" : "",
        endEdited: sub.wasEndEdited,
      };
    }

    const entry = row.entry;

    if (this.isTrailingOpenGeneralRow(row)) {
      return { startStr: this.formatTime(entry.startTime), startMarker: "", startEdited: false, separator: " –", endStr: "", endMarker: "", endEdited: false };
    }

    const startStr = this.formatTime(entry.startTime);
    const startMarker = showMarkers && boundary.start ? "*" : "";

    if (entry.isRunning || entry.endTime == null) {
      if (entry.type === "silentInterruption") {
        return { startStr, startMarker, startEdited: entry.wasStartEdited, separator: "", endStr: "", endMarker: "", endEdited: false };
      }
      return { startStr, startMarker, startEdited: entry.wasStartEdited, separator: " –", endStr: "", endMarker: "", endEdited: false };
    }

    const cappedEnd = this.getCappedDateInfo(entry.endTime);
    const hasEndMarker = showMarkers && (cappedEnd.clamped || entry.wasDayClamped || boundary.end);

    return {
      startStr,
      startMarker,
      startEdited: entry.wasStartEdited,
      separator: " – ",
      endStr: this.formatTime(cappedEnd.value),
      endMarker: hasEndMarker ? "*" : "",
      endEdited: entry.wasEndEdited,
    };
  }

  rowTimeRange(row: TableRow): string {
    const d = this.getRowTimeRangeData(row);
    return `${d.startStr}${d.startMarker}${d.separator}${d.endStr}${d.endMarker}`;
  }

  /** Formatted start time string for the row's time range cell. */
  rowStartTimePart(row: TableRow): string { return this.getRowTimeRangeData(row).startStr; }
  /** Overlap / clamp marker for the start time ("*" or ""). */
  rowStartMarkerPart(row: TableRow): string { return this.getRowTimeRangeData(row).startMarker; }
  /** Whether the start time was manually set by the user (renders in red). */
  rowStartIsEdited(row: TableRow): boolean { return this.getRowTimeRangeData(row).startEdited; }
  /** Separator string between start and end (" – ", " –", or ""). */
  rowSeparatorPart(row: TableRow): string { return this.getRowTimeRangeData(row).separator; }
  /** Formatted end time string, or "" when the entry is open-ended. */
  rowEndTimePart(row: TableRow): string { return this.getRowTimeRangeData(row).endStr; }
  /** Overlap / clamp marker for the end time ("*" or ""). */
  rowEndMarkerPart(row: TableRow): string { return this.getRowTimeRangeData(row).endMarker; }
  /** Whether the end time was manually set by the user (renders in red). */
  rowEndIsEdited(row: TableRow): boolean { return this.getRowTimeRangeData(row).endEdited; }

  /** Inline style applied to user-edited timestamps in row 1. */
  get editedTimeStyle(): Record<string, string> {
    return { color: COLOR_TIME_TRACKING_EDITED_TIME };
  }

  rowDuration(row: TableRow): string {
    if (this.isTrailingOpenGeneralRow(row)) return "";

    // Open entries (no end time) show "start –" with no duration — this includes
    // running project work, open sub-entries, and open pauses (User- and Project-level).
    if (row.subEntry ? row.subEntry.endTime == null : row.entry.endTime == null) return "";

    const cappedNow = this.getCappedDateInfo(this.now);
    const boundary = this.row1ProjectBoundaryMarkers[row.entry.id] ?? { start: false, end: false };
    // IRES-only: all `*` verification markers are suppressed for standard clients.
    const showMarkers = this.showVerificationMarkers;
    if (row.subEntry) {
      const isEntryStartBoundary =
        !!row.entry.startTime &&
        !!row.subEntry.startTime &&
        row.subEntry.startTime.getTime() === row.entry.startTime.getTime();
      const isEntryEndBoundary =
        !!row.entry.endTime &&
        !!row.subEntry.endTime &&
        row.subEntry.endTime.getTime() === row.entry.endTime.getTime();
      const end = row.subEntry.endTime
        ? this.getCappedDateInfo(row.subEntry.endTime)
        : cappedNow;
      const mins = calcDurationMinutes(row.subEntry.startTime, end.value);
      const inheritedEntryMarker = row.subEntry.wasMergedBoundaryAdjusted === true;
      const marker =
        showMarkers &&
        (end.clamped ||
        row.subEntry.wasDayClamped ||
        inheritedEntryMarker ||
        (boundary.start && isEntryStartBoundary) ||
        (boundary.end && isEntryEndBoundary))
          ? "*"
          : "";
      return `${formatDuration(row.subEntry.endTime == null ? Math.max(0, mins) : mins)}${marker}`;
    }
    const entry = row.entry;
    if (entry.type === "silentInterruption") return "";
    const end = entry.endTime
      ? this.getCappedDateInfo(entry.endTime)
      : cappedNow;
    const mins = calcDurationMinutes(entry.startTime, end.value);
    const marker = showMarkers && (end.clamped || entry.wasDayClamped || boundary.start || boundary.end) ? "*" : "";
    return `${formatDuration(entry.endTime == null ? Math.max(0, mins) : mins)}${marker}`;
  }

  /** Phase chip label — sub-entries show their label, project entries show phase, others show category. */
  rowPhaseLabel(row: TableRow): string {
    if (row.subEntry) {
      return row.subEntry.label;
    }
    const entry = row.entry;
    if (entry.type === "project" && entry.phaseLabel) {
      return this.$t(entry.phaseLabel) as string;
    }
    switch (entry.type) {
      case "pause": return this.$t("timeTrackingHistory.categories.pause") as string;
      case "travel": return this.$t("timeTrackingHistory.categories.travelingTime") as string;
      case "general": return this.$t("timeTrackingHistory.categories.general") as string;
      case "interruption": return this.$t("timeTrackingHistory.categories.interruption") as string;
      case "silentInterruption": return this.$t("timeTrackingHistory.categories.interruption") as string;
      case "other": return this.$t("timeTrackingHistory.categories.internal") as string;
      default: return "";
    }
  }

  /**
   * Project/task chip color by lifecycle state: light green while the entry is still
   * active (RUNNING or SUSPENDED), dark green once FINISHED. Unknown state (e.g. a
   * non-project entry) keeps the dark-green default. Pause/travel chips are unaffected.
   */
  /**
   * Travel chip colour by lifecycle state: pale while the drive is running or was interrupted, solid once
   * it arrived. Same convention as {@link projectStateChipColor}, so "not concluded" reads the same way
   * whichever kind of entry it is.
   *
   * An absent state means a drive logged before IRES Work recorded one, and those keep the historical
   * solid appearance rather than being painted as interrupted.
   */
  private travelStateChipColor(state?: TimeTrackingEntryState): string {
    return state === TimeTrackingEntryState.RUNNING || state === TimeTrackingEntryState.SUSPENDED
      ? COLOR_TIME_TRACKING_CHIP_TRAVEL_ACTIVE
      : COLOR_TIME_TRACKING_CHIP_TRAVEL;
  }

  private projectStateChipColor(state?: TimeTrackingEntryState): string {
    return state === TimeTrackingEntryState.RUNNING || state === TimeTrackingEntryState.SUSPENDED
      ? COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE
      : COLOR_TIME_TRACKING_CHIP_PROJECT;
  }

  rowChipColor(row: TableRow): string {
    if (row.subEntry) {
      switch (row.subEntry.type) {
        case "pause": return COLOR_TIME_TRACKING_CHIP_PAUSE;
        // Project sub-entry travel (from a projectEntries `countsAsTravel` entry) is green.
        case "travel": return COLOR_TIME_TRACKING_CHIP_TRAVEL_PROJECT;
        // Work sub-entry inherits its parent project entry's lifecycle state.
        default: return this.projectStateChipColor(row.entry.state);
      }
    }
    switch (row.entry.type) {
      case "project": return this.projectStateChipColor(row.entry.state);
      case "general": return COLOR_TIME_TRACKING_CHIP_GENERAL;
      case "pause": return COLOR_TIME_TRACKING_CHIP_PAUSE;
      // Top-level travel (from otherEntries) is not project-related → yellow, and pale while the drive
      // is running or was interrupted. IRES Work records `endReason` on a drive since 2026-08-19
      // (UNTERBRECHEN leaves SUSPENDED, ANGEKOMMEN COMPLETED); before this the two looked identical here,
      // which is exactly what Hans reported.
      case "travel": return this.travelStateChipColor(row.entry.state);
      case "interruption": return COLOR_TIME_TRACKING_CHIP_INTERRUPTION;
      case "silentInterruption": return COLOR_TIME_TRACKING_CHIP_INTERRUPTION;
      case "other": return COLOR_TIME_TRACKING_CHIP_TRAVEL;
      default: return COLOR_TIME_TRACKING_CHIP_INTERRUPTION;
    }
  }

  /**
   * Text color for a Row 1 chip. A dark tone on the two pale active-state chips (white on either would be
   * unreadable); white on every other (solid) chip. Derived from the resolved background so it can never
   * drift from `rowChipColor`.
   */
  rowChipTextColor(row: TableRow): string {
    switch (this.rowChipColor(row)) {
      case COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE:
        return COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE_TEXT;
      case COLOR_TIME_TRACKING_CHIP_TRAVEL_ACTIVE:
        return COLOR_TIME_TRACKING_CHIP_TRAVEL_ACTIVE_TEXT;
      default:
        return "white";
    }
  }

  /**
   * Description text — "Stille Unterbrechung" for a silently-interrupted entry/sub-entry
   * (an open entry closed by an unpaired S_SUSPEND), else the entry label for
   * travel/interruption/other; empty for normal project/general/pause rows.
   */
  rowDescription(row: TableRow): string {
    if (row.subEntry) {
      return row.subEntry.silentlyInterrupted
        ? (this.$t("timeTrackingHistory.categories.silentInterruption") as string)
        : "";
    }
    const entry = row.entry;
    if (entry.silentlyInterrupted) {
      return this.$t("timeTrackingHistory.categories.silentInterruption") as string;
    }
    switch (entry.type) {
      case "travel":
      case "interruption":
      case "silentInterruption":
      case "other":
        return entry.label;
      default:
        return "";
    }
  }

  formatSummaryMinutes(minutes: number): string {
    return formatMinutesToHours(minutes, false, this.$i18n.locale);
  }

  // ── Row 2: Project log display helpers ──

  /**
   * The end shown for a project log: the user-edited override when present,
   * otherwise endedAtDate (which, on stale auto-close, holds the auto-proposed
   * end). Mirrors the user-level entry handling.
   */
  private projectLogEffectiveEndDate(log: ProjectTimeTrackingLog): Date | null {
    if (log.userSetEndOfWork) return log.userSetEndOfWork.toDate();
    if (log.endedAtDate) return log.endedAtDate.toDate();
    return null;
  }

  /** True when the project log's end was user-edited (rendered red, like row 1). */
  projectLogEndIsEdited(log: ProjectTimeTrackingLog): boolean {
    return log.userSetEndOfWork != null;
  }

  private isCrossDayProjectLog(log: ProjectTimeTrackingLog): boolean {
    const end = this.projectLogEffectiveEndDate(log);
    if (!log.startedAtDate || !end) return false;
    const start = log.startedAtDate.toDate();
    return (
      start.getFullYear() !== end.getFullYear() ||
      start.getMonth() !== end.getMonth() ||
      start.getDate() !== end.getDate()
    );
  }

  private isAnomalousProjectLog(log: ProjectTimeTrackingLog): boolean {
    return this.isCrossDayProjectLog(log);
  }

  projectLogDate(log: ProjectTimeTrackingLog): string {
    if (!log.startedAtDate) return "–";
    const start = log.startedAtDate.toDate();
    const end = this.projectLogEffectiveEndDate(log);
    if (!end) return formatJsDate(start, false);

    const startDate = formatJsDate(start, false);
    const endDate = formatJsDate(end, false);
    if (!this.isCrossDayProjectLog(log)) {
      return startDate;
    }
    // Cross-day range keeps showing; only the `*` marker glyph is IRES-only.
    return `${startDate} – ${endDate}${this.showVerificationMarkers ? "*" : ""}`;
  }

  projectLogDateReason(log: ProjectTimeTrackingLog): string | null {
    if (!this.showVerificationMarkers) return null;
    if (!this.isCrossDayProjectLog(log)) return null;
    return this.markerReason("crossDay");
  }

  /** Start time + overlap marker for the project log time range (row 2). */
  projectLogStartLabel(log: ProjectTimeTrackingLog): string {
    const overlap = this.row2ProjectLogBoundaryMarkers[log.id] ?? { start: false, end: false };
    const start = log.startedAtDate ? formatJsTime(log.startedAtDate.toDate()) : "–";
    return `${start}${this.showVerificationMarkers && overlap.start ? "*" : ""}`;
  }

  /** End time (effective end) for the project log range, or null when still open. */
  projectLogEndTime(log: ProjectTimeTrackingLog): string | null {
    const end = this.projectLogEffectiveEndDate(log);
    return end ? formatJsTime(end) : null;
  }

  /** Overlap/cross-day marker suffix for the end (separate from the edited-red styling). */
  projectLogEndMarker(log: ProjectTimeTrackingLog): string {
    if (!this.showVerificationMarkers) return "";
    if (!this.projectLogEffectiveEndDate(log)) return "";
    const overlap = this.row2ProjectLogBoundaryMarkers[log.id] ?? { start: false, end: false };
    return overlap.end || this.isAnomalousProjectLog(log) ? "*" : "";
  }

  projectLogTimeRangeReason(log: ProjectTimeTrackingLog): string | null {
    if (!this.showVerificationMarkers) return null;
    const reasons: string[] = [];
    const overlap = this.row2ProjectLogBoundaryMarkers[log.id] ?? { start: false, end: false };
    if (overlap.start) reasons.push(this.markerReason("overlapStart"));
    if (overlap.end) reasons.push(this.markerReason("overlapEnd"));
    if (this.isAnomalousProjectLog(log)) reasons.push(this.markerReason("crossDay"));
    return this.formatMarkerReasonList(reasons);
  }

  projectLogDuration(log: ProjectTimeTrackingLog): string {
    if (!log.startedAtDate) return "–";
    const end = this.projectLogEffectiveEndDate(log);
    // Open project logs (no end time) show "start –" with no duration.
    if (!end) return "";
    const overlap = this.row2ProjectLogBoundaryMarkers[log.id] ?? { start: false, end: false };
    const start = log.startedAtDate.toDate();
    const mins = calcDurationMinutes(start, end);
    const marker = this.showVerificationMarkers && (this.isAnomalousProjectLog(log) || overlap.start || overlap.end) ? "*" : "";
    return `${formatDuration(mins)}${marker}`;
  }

  projectLogDurationReason(log: ProjectTimeTrackingLog): string | null {
    if (!this.showVerificationMarkers) return null;
    const reasons: string[] = [];
    const overlap = this.row2ProjectLogBoundaryMarkers[log.id] ?? { start: false, end: false };
    if (overlap.start) reasons.push(this.markerReason("overlapStart"));
    if (overlap.end) reasons.push(this.markerReason("overlapEnd"));
    if (this.isAnomalousProjectLog(log)) reasons.push(this.markerReason("crossDay"));
    return this.formatMarkerReasonList(reasons);
  }

  projectLogChipColor(log: ProjectTimeTrackingLog): string {
    if (log.countsAsPause) return COLOR_TIME_TRACKING_CHIP_PAUSE;
    // Project logs' travel is always "Anfahrt" (to the project) → green.
    if (log.countsAsTravel) return COLOR_TIME_TRACKING_CHIP_TRAVEL_PROJECT;
    // Work log: light green while active (running/suspended), dark green once finished.
    return this.projectStateChipColor(
      deriveEntryState(this.projectLogEffectiveEndDate(log), log.endReason)
    );
  }

  /** Row 2 chip text color: dark green on the pale active chip, white otherwise. */
  projectLogChipTextColor(log: ProjectTimeTrackingLog): string {
    return this.projectLogChipColor(log) === COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE
      ? COLOR_TIME_TRACKING_CHIP_PROJECT_ACTIVE_TEXT
      : "white";
  }

  projectLogPhaseLabel(log: ProjectTimeTrackingLog): string {
    return this.$t(getProjectLogTaskLabel(log)) as string;
  }

  /**
   * Maps a raw source string to a display letter: P (Project), W (Work), O (Office/Portal),
   * C (Cloud/implicit — e.g. a silently auto-closed stale entry), or –.
   */
  private formatSource(source: string | null | undefined): string {
    if (source === ProjectOperationSource.PROJECT_MANAGER_APP) return "P";
    if (source === ProjectOperationSource.DEVICE_MANAGER_APP) return "W";
    if (source === ProjectOperationSource.PORTAL) return "O";
    if (source === ProjectOperationSource.CLOUD_FUNCTION) return "C";
    return "–";
  }

  /**
   * Returns the source label for a Row 1 entry.
   * Format: "startSource/endSource" (e.g. "W/P"), or "–" when both are unknown.
   */
  rowSource(row: TableRow): string {
    // A silent interruption is a single suspend event (not an open/close pair), so
    // show just the one source letter from its operationSource.
    if (!row.subEntry && row.entry.type === "silentInterruption") {
      return this.formatSource(row.entry.startSource);
    }
    const start = this.formatSource(
      row.subEntry ? row.subEntry.startSource : row.entry.startSource
    );
    const end = this.formatSource(
      row.subEntry ? row.subEntry.endSource : row.entry.endSource
    );
    if (start === "–" && end === "–") return "–";
    return `${start}/${end}`;
  }

  /**
   * Returns the source label for a Row 2 project log.
   * Format: "startSource/endSource" (e.g. "W/P"), or "–" when both are unknown.
   */
  projectLogSource(log: ProjectTimeTrackingLog): string {
    const start = this.formatSource(log.startSource);
    const end = this.formatSource(log.endSource);
    if (start === "–" && end === "–") return "–";
    return `${start}/${end}`;
  }

  resolveUserName(userId: string): string {
    if (this.userNameCache[userId]) return this.userNameCache[userId];
    const user = findUser(userId);
    const name = user?.nickname || userId;
    this.userNameCache[userId] = name;
    return name;
  }

  /** Resolves a project name by ID from cached project overviews. Falls back to Firestore fetch. */
  resolveProjectName(projectId: string, fallbackName?: string | null): string {
    if (this.projectNameCache[projectId]) return this.projectNameCache[projectId];

    // Check Vuex store cache (running + recently finished projects)
    const allOverviews = [
      ...ProjectsModule.runningDryingProjects,
      ...ProjectsModule.recentlyFinishedDryingProjects,
      ...ProjectsModule.finishedDryingProjects,
      ...ProjectsModule.runningLeakDetectionProjects,
      ...ProjectsModule.recentlyFinishedLeakDetectionProjects,
      ...ProjectsModule.finishedLeakDetectionProjects,
    ];
    const overview = allOverviews.find((p) => p.id === projectId);
    if (overview?.projectName) {
      this.projectNameCache[projectId] = overview.projectName;
      return overview.projectName;
    }

    // If fallback name is available, use it while async fetch runs
    if (fallbackName) {
      this.projectNameCache[projectId] = fallbackName;
    }

    // Async fetch from Firestore (updates cache reactively via Vue.set)
    this.fetchProjectName(projectId);

    return this.projectNameCache[projectId] || projectId;
  }

  /** Resolves preferred project number for Row 2: office number -> IRES number. */
  resolveProjectNumber(projectId: string): string | null {
    if (this.projectNumberCache[projectId]) return this.projectNumberCache[projectId];

    const allOverviews = [
      ...ProjectsModule.runningDryingProjects,
      ...ProjectsModule.recentlyFinishedDryingProjects,
      ...ProjectsModule.finishedDryingProjects,
      ...ProjectsModule.runningLeakDetectionProjects,
      ...ProjectsModule.recentlyFinishedLeakDetectionProjects,
      ...ProjectsModule.finishedLeakDetectionProjects,
    ];
    const overview = allOverviews.find((p) => p.id === projectId);
    const preferredNumber = this.getPreferredProjectNumber(
      overview?.projectNumber,
      overview?.projectNumberIres,
    );

    if (preferredNumber) {
      this.projectNumberCache[projectId] = preferredNumber;
      return preferredNumber;
    }

    this.fetchProjectNumber(projectId);
    return null;
  }

  /** Resolves the Row 1 project label with priority: projectNumber -> projectNumberIres -> fallback name. */
  resolveRow1ProjectLabel(projectId: string, fallbackName?: string | null): string {
    if (this.row1ProjectLabelCache[projectId]) return this.row1ProjectLabelCache[projectId];

    const allOverviews = [
      ...ProjectsModule.runningDryingProjects,
      ...ProjectsModule.recentlyFinishedDryingProjects,
      ...ProjectsModule.finishedDryingProjects,
      ...ProjectsModule.runningLeakDetectionProjects,
      ...ProjectsModule.recentlyFinishedLeakDetectionProjects,
      ...ProjectsModule.finishedLeakDetectionProjects,
    ];
    const overview = allOverviews.find((p) => p.id === projectId);
    const overviewLabel = this.getPreferredProjectLabel(
      overview?.projectNumber,
      overview?.projectNumberIres,
      overview?.projectName,
    );
    if (overviewLabel) {
      this.row1ProjectLabelCache[projectId] = overviewLabel;
      return overviewLabel;
    }

    const fallbackLabel = this.getPreferredProjectLabel(undefined, undefined, fallbackName ?? undefined);
    if (fallbackLabel) {
      this.row1ProjectLabelCache[projectId] = fallbackLabel;
    }

    this.fetchRow1ProjectLabel(projectId);

    return this.row1ProjectLabelCache[projectId] || projectId;
  }

  private getPreferredProjectLabel(
    projectNumber?: string | null,
    projectNumberIres?: string | null,
    fallbackName?: string | null,
  ): string | null {
    const preferredNumber = this.getPreferredProjectNumber(projectNumber, projectNumberIres);
    if (preferredNumber) return preferredNumber;

    const fallback = fallbackName?.trim();
    if (fallback) return fallback;

    return null;
  }

  private getPreferredProjectNumber(
    projectNumber?: string | null,
    projectNumberIres?: string | null,
  ): string | null {
    const officeNumber = projectNumber?.trim();
    if (officeNumber) return officeNumber;

    const iresNumber = projectNumberIres?.trim();
    if (iresNumber) return iresNumber;

    return null;
  }

  /**
   * A day can hold entries of a project that has since been archived or deleted, and those
   * still have to show their number instead of a bare name or id, so every label lookup here
   * reads the archived copy as well (`returnArchivedProjectDetails`).
   */
  private async fetchRow1ProjectLabel(projectId: string) {
    if (this.row1ProjectLabelCache[projectId]) return;
    const result = await getProjectDetails(projectId, true);
    if (typeof result !== "string" && result.clientData) {
      const resolved = this.getPreferredProjectLabel(
        result.clientData.projectNumber,
        result.clientData.projectNumberIres,
        result.clientData.projectName,
      );
      if (resolved) {
        this.$set(this.row1ProjectLabelCache, projectId, resolved);
      }
    }
  }

  private async fetchProjectName(projectId: string) {
    if (this.projectNameCache[projectId]) return;
    const result = await getProjectDetails(projectId, true);
    if (typeof result !== "string" && result.clientData?.projectName) {
      this.$set(this.projectNameCache, projectId, result.clientData.projectName);
    }
  }

  private async fetchProjectNumber(projectId: string) {
    if (this.projectNumberCache[projectId]) return;
    const result = await getProjectDetails(projectId, true);
    if (typeof result !== "string" && result.clientData) {
      const preferredNumber = this.getPreferredProjectNumber(
        result.clientData.projectNumber,
        result.clientData.projectNumberIres,
      );
      if (preferredNumber) {
        this.$set(this.projectNumberCache, projectId, preferredNumber);
      }
    }
  }

  // ── Row 1 → Row 2 interaction ──

  /** Clicking a project row in Row 1 switches Row 2 to that project. */
  onRowClick(row: TableRow) {
    const projectId = row.entry.projectId;
    if (!projectId) return;

    // If clicking the already-displayed default project, do nothing
    const defaultId = this.activeProjectId ?? this.lastProjectId;
    if (projectId === defaultId && !this.clickedProjectId) return;

    this.clickedProjectId = projectId;
    this.clickedProjectName = row.projectName ?? null;
    this.subscribeToDisplayedProject();
  }

  /** Returns to the auto-detected active/last project. */
  clearClickedProject() {
    this.clickedProjectId = null;
    this.clickedProjectName = null;
    this.subscribeToDisplayedProject();
  }

  // ── Project details navigation ──

  /**
   * Whether a project's details screen can be opened from here. Mirrors the IRES device overview
   * list: requires client project access and is disabled on devices-only portals. For the user whose
   * view this shows, the chosen one while "Sicht auf" is active (ticket 2610052), as the route guard.
   */
  get canNavigateToProject(): boolean {
    return (
      manageProjectsClientAccess(getEffectiveUser()) &&
      getProjectManagementType() !== ProjectManagement.DEVICES_ONLY_WEBSITE
    );
  }

  /**
   * Opens the project details screen for the given project, matching the device
   * overview list navigation (`/main/projects/:projectId`). No-op when navigation
   * is not allowed or no project id is available.
   */
  openProjectDetails(projectId: string | null | undefined) {
    if (!projectId || !this.canNavigateToProject) return;
    router.push({ path: `/main/projects/${projectId}` });
  }

  /**
   * Row 1 project-chip click. When navigation is allowed, opens project details and
   * stops propagation so the row's `onRowClick` (Row-2 project switch) does not also
   * fire. When not allowed, the click falls through so the row still switches Row 2.
   */
  onRow1ProjectChipClick(event: Event, projectId: string | null | undefined) {
    if (!this.canNavigateToProject) return;
    event.stopPropagation();
    this.openProjectDetails(projectId);
  }

  // ── Target user (Sicht auf) ──

  async onToggleTargetUser() {
    if (this.hasSelectedTargetUser) {
      endViewAs();
    } else {
      await this.onPickTargetUser();
    }
  }

  async onPickTargetUser() {
    const selectedUser = await this.userPickerDialog.show(
      this.selectableTargetUsers
    );

    if (selectedUser) {
      startViewAs(selectedUser);
    }
  }

  get targetUserTooltip(): string {
    return this.hasSelectedTargetUser
      ? this.$t("cockpit.iresExtra.backToUser", [
          this.activeUser?.nickname ?? "",
        ]).toString()
      : this.$t("cockpit.iresExtra.selectUser").toString();
  }

  // ── Row 2: Project subscription management ──

  private teardownProjectSubscriptions() {
    if (this.unsubscribeDisplayedProject) {
      this.unsubscribeDisplayedProject();
      this.unsubscribeDisplayedProject = null;
    }
  }

  /** Subscribes to logs for the currently displayed project. */
  private subscribeToDisplayedProject() {
    this.teardownProjectSubscriptions();
    this.displayedProjectLogs = [];
    this.displayedProjectLogsSource = ProjectTimeTrackingLogsSource.LIVE;

    const projectId = this.displayedProjectId;
    if (!projectId) {
      this.isLoadingDisplayedProject = false;
      return;
    }

    this.isLoadingDisplayedProject = true;
    this.unsubscribeDisplayedProject = onProjectTimeTrackingLogsSnapshot(
      projectId,
      (result) => {
        // Row 2 shows started project log entries, including still-open ones.
        this.displayedProjectLogs = result.logs.filter(
          (log) => !!log.startedAtDate
        );
        this.displayedProjectLogsSource = result.source;
        this.isLoadingDisplayedProject = false;
        this.scrollToBottom("row2Table");
      }
    );
  }

  private updateProjectSubscriptions(dayEntry: TimeTrackingDayEntry | null) {
    if (!dayEntry) {
      this.teardownProjectSubscriptions();
      this.activeProjectId = null;
      this.activeProjectName = null;
      this.lastProjectId = null;
      this.lastProjectName = null;
      this.displayedProjectLogs = [];
      this.displayedProjectLogsSource = ProjectTimeTrackingLogsSource.LIVE;
      this.isLoadingDisplayedProject = false;
      return;
    }

    const result = detectActiveAndLastProjectFromDay(dayEntry);

    const activeChanged = result.activeProjectId !== this.activeProjectId;
    const lastChanged = result.lastProjectId !== this.lastProjectId;

    this.activeProjectId = result.activeProjectId;
    this.activeProjectName = result.activeProjectName;
    this.lastProjectId = result.lastProjectId;
    this.lastProjectName = result.lastProjectName;

    // Only re-subscribe if the displayed project changed (and no clicked override)
    if (!this.clickedProjectId && (activeChanged || lastChanged)) {
      this.subscribeToDisplayedProject();
    }
  }
}
